// @flow
import React from 'react';
import { Helmet } from 'react-helmet';

// Material
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Paper from '@material-ui/core/Paper';

// Custom
import SimpleBreadcrumbs from '../components/ui/SimpleBreadcrumbs';

// Data
const links = [{ name: 'Privacy policy' }];

const useStyles = makeStyles(theme => ({
  page: {
    padding: theme.spacing(3, 2),
  },
  link: {
    textDecoration: 'none !important',
  },
}));

const Terms = () => {
  const classes = useStyles();
  const privacyUrl = 'https://www.naexguide.com/privacy-policy';

  return (
    <React.Fragment>
      <Helmet>
        <title>Privacy policy | Naexguide</title>
        <meta property="og:url" content={privacyUrl} />
        <link rel="canonical" href={privacyUrl} />
      </Helmet>
      <Container>
        <SimpleBreadcrumbs links={links} />
        <Paper className={classes.page}>
          <h1>Privacy Policy</h1>

          <p>Last updated: December 14, 2019</p>

          <p>
            Naexguide ("us", "we", or "our") operates https://naexguide.com (the
            "Site"). This page informs you of our policies regarding the
            collection, use and disclosure of Personal Information we receive
            from users of the Site.
          </p>

          <p>
            We use your Personal Information only for providing and improving
            the Site. By using the Site, you agree to the collection and use of
            information in accordance with this policy.
          </p>

          <h2>Information Collection And Use</h2>

          <p>
            While using our Site, we may or may not ask you to provide us with
            certain personally identifiable information that can be used to
            contact or identify you. Personally identifiable information may
            include, but is not limited to information collected by Google
            Analytics ("Personal Information").
          </p>

          <h2>Log Data</h2>

          <p>
            Like many site operators, we collect information that your browser
            sends whenever you visit our Site ("Log Data").
          </p>

          <p>
            This Log Data may include information such as your computer's
            Internet Protocol ("IP") address, browser type, browser version, the
            pages of our Site that you visit, the time and date of your visit,
            the time spent on those pages and other statistics.
          </p>

          <p>
            In addition, we may use third party services such as Google
            Analytics that collect, monitor and analyze this information.
          </p>

          <p>
            The Log Data section is for businesses that use analytics or
            tracking services in websites or apps, like Google Analytics.
          </p>

          <h2>GDPR Privacy Policy</h2>

          <h3>Definitions</h3>

          <p>
            Naexguide ("us", "we", or "our") operates https://naexguide.com (the
            "Site")
          </p>

          <p>GDPR</p>
          <p>General Data Protection Regulation Act.</p>

          <h3>Data Controller</h3>

          <p>
            Data Controller means the natural or legal person who (either alone
            or jointly or in common with other persons) determines the purposes
            for which and the manner in which any personal information are, or
            are to be, processed.
          </p>

          <h3>Data Processor</h3>

          <p>
            Data Processor means any natural or legal person who processes the
            data on behalf of the Data Controller.
          </p>

          <h3>Data Subject</h3>

          <p>
            Data Subject is any living individual who is using our Service and
            is the subject of Personal Data.
          </p>

          <h3>1. Principles for processing personal data</h3>

          <p>Our principles for processing personal data are:</p>

          <p>
            Fairness and lawfulness. When we process personal data, the
            individual rights of the Data Subjects must be protected. All
            personal data must be collected and processed in a legal and fair
            manner.
          </p>

          <p>
            Restricted to a specific purpose. The personal data of Data Subject
            must be processed only for specific purposes.
          </p>

          <p>
            Transparency. The Data Subject must be informed of how his/her data
            is being collected, processed and used.
          </p>

          <h3>2. What personal data we collect and process</h3>

          <p>
            Naexguide collects several different types of personal data for
            various purposes. Personal Data may include, but is not limited to:
          </p>
          <ul>
            <li>The HTTP request of the user</li>
            <li>Browser/system information</li>
            <li>First-party cookies</li>
          </ul>

          <h3>3. How we use the personal data</h3>

          <p>
            Naexguide uses the collected personal data for various purposes,
            including but not limited to the following:
          </p>

          <ul>
            <li>To provide you with services</li>
            <li>To notify you about changes to our services and/or products</li>
            <li>To provide customer support</li>
            <li>
              To gather analysis or valuable information so that we can improve
              our services
            </li>
            <li>To detect, prevent and address technical issues</li>
          </ul>

          <h3>4. Legal basis for collecting and processing personal data</h3>

          <p>
            Naexguide legal basis for collecting and using the personal data
            described in this Data Protection Policy depends on the personal
            data we collect and the specific context in which we collect the
            information:
          </p>

          <ul>
            <li>Naexguide needs to perform a contract with you</li>
            <li>You have given Naexguide permission to do so</li>
            <li>
              Processing your personal data is in Naexguide legitimate interests
            </li>
            <li>Naexguide needs to comply with the law</li>
          </ul>

          <h3>5. Retention of personal data</h3>

          <p>
            Naexguide will retain your personal information only for as long as
            is necessary for the purposes set out in this Data Protection
            Policy.
          </p>

          <p>
            Naexguide will retain and use your information to the extent
            necessary to comply with our legal obligations, resolve disputes,
            and enforce our policies.
          </p>

          <h3>6. Data protection rights</h3>

          <p>
            If you are a resident of the European Economic Area (EEA), you have
            certain data protection rights. If you wish to be informed what
            personal data we hold about you and if you want it to be removed
            from our systems, please contact us.
          </p>

          <p>
            In certain circumstances, you have the following data protection
            rights:
          </p>

          <ul>
            <li>
              The right to access, update or to delete the information we have
              on you
            </li>
            <li>The right of rectification</li>
            <li>The right to object</li>
            <li>The right of restriction</li>
            <li>The right to data portability</li>
            <li>The right to withdraw consent</li>
          </ul>

          <h2>Cookies</h2>

          <p>
            Cookies are files with small amount of data, which may include an
            anonymous unique identifier. Cookies are sent to your browser from a
            web site and stored on your computer's hard drive.
          </p>

          <p>
            Like many sites, we use "cookies" to collect information. You can
            instruct your browser to refuse all cookies or to indicate when a
            cookie is being sent. However, if you do not accept cookies, you may
            not be able to use some portions of our Site.
          </p>

          <h2>Security</h2>

          <p>
            The security of your Personal Information is important to us, but
            remember that no method of transmission over the Internet, or method
            of electronic storage, is 100% secure. While we strive to use
            commercially acceptable means to protect your Personal Information,
            we cannot guarantee its absolute security.
          </p>

          <h2>Changes To This Privacy Policy or This GDPR Privacy Policy</h2>

          <p>
            This Privacy Policy is effective as of December 14, 2019 and will
            remain in effect except with respect to any changes in its
            provisions in the future, which will be in effect immediately after
            being posted on this page.
          </p>

          <p>
            We reserve the right to update or change our Privacy Policy at any
            time and you should check this Privacy Policy periodically. Your
            continued use of the Service after we post any modifications to the
            Privacy Policy on this page will constitute your acknowledgment of
            the modifications and your consent to abide and be bound by the
            modified Privacy Policy.
          </p>

          <p>
            If we make any material changes to this Privacy Policy, we will
            notify you by placing a prominent notice on our website.
          </p>

          <h2>Contact Us</h2>

          <p>
            If you have any questions about this Privacy Policy, please contact
            us.
          </p>
        </Paper>
      </Container>
    </React.Fragment>
  );
};

export default Terms;
